// Firestore Collection Service Factory
// Based on brain pattern: Firestore Collection Service Factory
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
} from 'firebase/firestore';
import type { QueryConstraint, Unsubscribe } from 'firebase/firestore';
import { db } from './config';

// Re-export query helpers for convenience
export { where, orderBy, limit, increment };

// Generic Firestore operations factory
export const createCollectionService = <T extends { id: string }>(
  collectionName: string
) => {
  const collectionRef = collection(db, collectionName);

  return {
    // Get single document by ID
    getById: async (id: string): Promise<T | null> => {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;
      return { id: docSnap.id, ...docSnap.data() } as T;
    },

    // Get all documents with optional constraints
    getAll: async (constraints: QueryConstraint[] = []): Promise<T[]> => {
      const q = query(collectionRef, ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as T);
    },

    // Subscribe to real-time updates
    subscribe: (
      constraints: QueryConstraint[],
      callback: (data: T[]) => void,
      onError?: (error: Error) => void
    ): Unsubscribe => {
      const q = query(collectionRef, ...constraints);
      return onSnapshot(
        q,
        (snapshot) => {
          callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as T));
        },
        onError
      );
    },

    // Create new document
    create: async (
      data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>,
      userId: string
    ): Promise<string> => {
      const docData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
      };
      const docRef = await addDoc(collectionRef, docData);
      return docRef.id;
    },

    // Create with specific ID
    createWithId: async (
      id: string,
      data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>,
      userId: string
    ): Promise<void> => {
      const docRef = doc(db, collectionName, id);
      const docData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
      };
      await updateDoc(docRef, docData).catch(() => {
        // If doc doesn't exist, create it
        return addDoc(collectionRef, { ...docData, id });
      });
    },

    // Update existing document
    update: async (id: string, data: Partial<T>): Promise<void> => {
      const docRef = doc(db, collectionName, id);
      await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
    },

    // Delete document
    delete: async (id: string): Promise<void> => {
      await deleteDoc(doc(db, collectionName, id));
    },

    // Get collection reference for complex queries
    getCollectionRef: () => collectionRef,
  };
};

// Organization-scoped collection service (for multi-tenant data)
export const createOrgCollectionService = <T extends { id: string; organizationId: string }>(
  collectionName: string
) => {
  const baseService = createCollectionService<T>(collectionName);

  return {
    ...baseService,

    // Get all for organization
    getByOrg: async (orgId: string, constraints: QueryConstraint[] = []): Promise<T[]> => {
      return baseService.getAll([where('organizationId', '==', orgId), ...constraints]);
    },

    // Subscribe to org-scoped data
    subscribeByOrg: (
      orgId: string,
      constraints: QueryConstraint[],
      callback: (data: T[]) => void,
      onError?: (error: Error) => void
    ): Unsubscribe => {
      return baseService.subscribe(
        [where('organizationId', '==', orgId), ...constraints],
        callback,
        onError
      );
    },

    // Create with organization ID
    createForOrg: async (
      orgId: string,
      data: Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>,
      userId: string
    ): Promise<string> => {
      return baseService.create({ ...data, organizationId: orgId } as any, userId);
    },
  };
};

// Project-scoped collection service (for project-specific data like tickets)
export const createProjectCollectionService = <
  T extends { id: string; organizationId: string; projectId: string }
>(
  collectionName: string
) => {
  const orgService = createOrgCollectionService<T>(collectionName);

  return {
    ...orgService,

    // Get all for project
    getByProject: async (
      orgId: string,
      projectId: string,
      constraints: QueryConstraint[] = []
    ): Promise<T[]> => {
      return orgService.getAll([
        where('organizationId', '==', orgId),
        where('projectId', '==', projectId),
        ...constraints,
      ]);
    },

    // Subscribe to project-scoped data
    subscribeByProject: (
      orgId: string,
      projectId: string,
      constraints: QueryConstraint[],
      callback: (data: T[]) => void,
      onError?: (error: Error) => void
    ): Unsubscribe => {
      return orgService.subscribe(
        [where('organizationId', '==', orgId), where('projectId', '==', projectId), ...constraints],
        callback,
        onError
      );
    },

    // Create with project ID
    createForProject: async (
      orgId: string,
      projectId: string,
      data: Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'projectId'>,
      userId: string
    ): Promise<string> => {
      return orgService.create(
        { ...data, organizationId: orgId, projectId } as any,
        userId
      );
    },
  };
};

// Helper to convert Firestore Timestamp to Date
export const toDate = (timestamp: Timestamp | Date | undefined): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  return null;
};

// Helper to format date for display
export const formatDate = (timestamp: Timestamp | Date | undefined): string => {
  const date = toDate(timestamp);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper to format relative time
export const formatRelativeTime = (timestamp: Timestamp | Date | undefined): string => {
  const date = toDate(timestamp);
  if (!date) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(timestamp);
};
